<script setup lang="ts">
import { computed } from 'vue'
import { getLessonTimezone, lessonStart } from '@/lib/lessonTime'
import { Info, CalendarCheck } from 'lucide-vue-next'

interface TimeSlot {
  day: string
  time: string
  label: string
  is_available?: boolean
  slotTag?: string
  subjectId?: number
  subjectName?: string
  levelId?: number
  levelName?: string
}

interface Props {
  modelValue: {
    day: string
    time: string
    slotTag?: string
    subjectId?: number
    subjectName?: string
    levelId?: number
    levelName?: string
  }
  oneToOneAvailability?: TimeSlot[]
  firstLessonNoticeHours?: number
}

const props = defineProps<Props>()

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

/**
 * The first lesson is the first time the chosen weekday and UK time comes
 * round at least the notice period from now; a sooner one starts the following
 * week. The same rule the diary books by, so the date promised is the date.
 */
const firstLesson = computed(() => {
  const { day, time } = props.modelValue
  const noticeHours = props.firstLessonNoticeHours

  if (!day || !time || !noticeHours) return null

  const earliest = Date.now() + noticeHours * 3600 * 1000
  const todayInUk = new Intl.DateTimeFormat('en-CA', { timeZone: getLessonTimezone() }).format(new Date())
  let skipped = false

  for (let offset = 0; offset < 14; offset++) {
    const date = new Date(`${todayInUk}T12:00:00Z`)
    date.setUTCDate(date.getUTCDate() + offset)

    if (DAYS[date.getUTCDay()] !== day.toLowerCase()) continue

    const start = lessonStart(date.toISOString().slice(0, 10), time)
    if (!start || start.getTime() <= Date.now()) continue

    if (start.getTime() < earliest) {
      skipped = true
      continue
    }

    return {
      date: start.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: getLessonTimezone() }),
      skipped,
    }
  }

  return null
})
const emit = defineEmits<{
  'update:modelValue': [value: { day: string; time: string; slotTag?: string; subjectId?: number; subjectName?: string; levelId?: number; levelName?: string }]
}>()

const availableSlots = computed(() => (props.oneToOneAvailability || []).filter(s => s.is_available !== false))

function updateTimeSlot(event: Event) {
  const target = event.target as HTMLSelectElement
  const slot = availableSlots.value.find(s => `${s.day}-${s.time}` === target.value)

  if (slot) {
    emit('update:modelValue', {
      ...props.modelValue,
      day: slot.day,
      time: slot.time,
      slotTag: slot.slotTag,
      subjectId: slot.subjectId ?? props.modelValue.subjectId,
      subjectName: slot.subjectName ?? props.modelValue.subjectName,
      levelId: slot.levelId ?? props.modelValue.levelId,
      levelName: slot.levelName ?? props.modelValue.levelName,
    })
  } else {
    emit('update:modelValue', {
      ...props.modelValue,
      day: '',
      time: '',
      slotTag: undefined,
    })
  }
}

// Current selected slot value for the dropdown
const selectedSlotValue = computed(() => {
  if (props.modelValue.day && props.modelValue.time) {
    return `${props.modelValue.day}-${props.modelValue.time}`
  }
  return ''
})
</script>

<template>
  <section class="bg-white rounded-2xl shadow-sm p-4 md:p-8">
    <h2 class="text-xl font-semibold text-gray-900 mb-6">
      Select Your 1:1 Session Time
    </h2>

    <div class="space-y-6">
      <div v-if="availableSlots.length === 0" class="text-center py-6 bg-gray-50 rounded-xl">
        <p class="text-gray-500">No available time slots at the moment.</p>
        <p class="text-sm text-gray-400 mt-1">Please contact the tutor for availability.</p>
      </div>

      <div v-else>
        <label class="block text-sm font-semibold text-gray-700 mb-2">Select Time Slot</label>
        <select
          :value="selectedSlotValue"
          @change="updateTimeSlot"
          class="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-900"
        >
          <option value="">Choose a time slot</option>
          <option
            v-for="slot in availableSlots"
            :key="`${slot.day}-${slot.time}`"
            :value="`${slot.day}-${slot.time}`"
          >
            {{ slot.label }}
          </option>
        </select>
      </div>

      <!-- First lesson expectation, once a slot is chosen -->
      <div v-if="firstLesson" class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
        <p class="flex items-start text-emerald-900">
          <CalendarCheck class="w-4 h-4 mr-2 mt-0.5 shrink-0 text-emerald-600" />
          <span>
            Once your tutor confirms, your first lesson will be <strong>{{ firstLesson.date }}</strong>.
            <template v-if="firstLesson.skipped">
              This week's is less than {{ firstLessonNoticeHours }} hours away, so lessons start the week after.
            </template>
          </span>
        </p>
      </div>

      <!-- Info text -->
      <p class="text-xs text-gray-500 flex items-start">
        <Info class="w-4 h-4 mr-1.5 mt-0.5 shrink-0" />
        <span>
          Select your preferred time for your weekly 1:1 session. You can ask to change it later from your dashboard.
        </span>
      </p>
    </div>
  </section>
</template>
